import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { certificateService } from '@/services/certificate/certificate.service';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Upload, FileText, CheckCircle2 } from 'lucide-react';

// Keep in sync with the backend upload-template limit
const MAX_TEMPLATE_SIZE_MB = 100;

interface CertificateTemplateUploadProps {
  templateFileUrl?: string;
  onUploadComplete: (fileUrl: string) => void;
}

const CertificateTemplateUpload: React.FC<CertificateTemplateUploadProps> = ({
  templateFileUrl,
  onUploadComplete,
}) => {
  const { t } = useTranslation();
  const [uploading, setUploading] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (file.type !== 'application/pdf') {
      toast.error(t('certificate.templateUpload.pleaseUploadPdfFile'));
      return;
    }

    if (file.size > MAX_TEMPLATE_SIZE_MB * 1024 * 1024) {
      toast.error(t('certificate.templateUpload.fileTooLarge', { max: MAX_TEMPLATE_SIZE_MB }));
      return;
    }

    setUploadedFile(file);

    try {
      setUploading(true);
      const result = await certificateService.uploadTemplate(file);
      setUploadedFile(file);
      onUploadComplete(result.fileUrl);
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('certificate.templateUpload.failedToUploadTemplate'));
      setUploadedFile(null);
    } finally {
      setUploading(false);
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('certificate.templateUpload.title')}</CardTitle>
        <CardDescription>
          {t('certificate.templateUpload.description')}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf"
            onChange={handleFileSelect}
            className="hidden"
          />

          {templateFileUrl ? (
            <div className="p-6 border-2 border-[#001A55]/15 bg-[#F6F9FF] rounded-lg">
              <div className="flex items-center gap-3">
                <CheckCircle2 className="h-6 w-6 text-[#001A55]" />
                <div>
                  <p className="font-medium text-[#001A55]">{t('certificate.templateUpload.templateUploaded')}</p>
                  <p className="text-sm text-[#001A55]/70">
                    {uploadedFile?.name || t('certificate.templateUpload.templateFileIsReady')}
                  </p>
                </div>
              </div>
              <Button
                variant="outline"
                onClick={handleClick}
                className="mt-4"
                disabled={uploading}
              >
                <Upload className="h-4 w-4 mr-2" />
                {uploading ? t('certificate.templateUpload.uploading') : t('certificate.templateUpload.replaceTemplate')}
              </Button>
            </div>
          ) : (
            <div
              onClick={handleClick}
              className="border-2 border-dashed border-[#001A55]/20 rounded-lg p-12 text-center cursor-pointer hover:border-[#001A55]/40 hover:bg-[#F6F9FF] transition-colors"
            >
              <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-lg font-medium text-gray-700 mb-2">
                {t('certificate.templateUpload.clickToUploadPdfTemplate')}
              </p>
              <p className="text-sm text-gray-500">
                {t('certificate.templateUpload.pdfFilesOnly', { max: MAX_TEMPLATE_SIZE_MB })}
              </p>
              {uploading && (
                <p className="text-sm text-blue-600 mt-4">{t('certificate.templateUpload.uploading')}</p>
              )}
            </div>
          )}

          {uploadedFile && !templateFileUrl && (
            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">
                {t('certificate.templateUpload.selected')}: <span className="font-medium">{uploadedFile.name}</span>
              </p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default CertificateTemplateUpload;

